import { createConcurrencyQueue, type ConcurrencyQueue } from '@/lib/async-concurrency-queue'

/**
 * Cap parallel `/api/documents/explorer-tree` fetches across Browse spaces.
 * Hard reload previously N-way paralleled every workspace x space and helped
 * trigger Railway 15s 502s (browser surfaces those as CORS). See incident
 * 2026-07-19-production-spa-boot-502-cors-storm.
 */
export const EXPLORER_TREE_FETCH_CONCURRENCY = 2

let queue: ConcurrencyQueue = createConcurrencyQueue(EXPLORER_TREE_FETCH_CONCURRENCY)
/** treeKey -> in-flight (or queued) promise — coalesce duplicate schedules */
const inflightByKey = new Map<string, Promise<void>>()

/**
 * Schedule an explorer-tree network fetch. Same treeKey coalesces to one request.
 */
export function enqueueExplorerTreeFetch(
  treeKey: string,
  fetchFn: () => Promise<void>,
): Promise<void> {
  const existing = inflightByKey.get(treeKey)
  if (existing) return existing

  const promise = queue
    .run(async () => {
      await fetchFn()
    })
    .finally(() => {
      if (inflightByKey.get(treeKey) === promise) {
        inflightByKey.delete(treeKey)
      }
    })

  inflightByKey.set(treeKey, promise)
  return promise
}

/** Test helper — reset singleton queue + keys so suites stay isolated. */
export function __resetExplorerTreeFetchQueueForTests(): void {
  inflightByKey.clear()
  queue = createConcurrencyQueue(EXPLORER_TREE_FETCH_CONCURRENCY)
}
